import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Zap, Users, ChevronRight } from "lucide-react";
import { ScheduleDemo } from "@/components/sections";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { T, THtml } from "@/components/editable/T";
import i18next from "i18next";

const PAGE = "pricing";

i18next.use({
  type: "postProcessor",
  name: "pricingContactLink",
  process(value, _key, options) {
    const escapeHtml = (text: string) => text.replace(/[&<>"']/g, (character) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
    }[character] ?? character));
    const label = escapeHtml(String(options.contactLabel ?? ""));
    const href = escapeHtml(String(options.contactHref ?? ""));
    const text = escapeHtml(value);
    return label ? text.replace(label, `<a href="${href}" class="underline underline-offset-4">${label}</a>`) : text;
  },
});


interface FeatureDef {
  k: string;
  d: string;
  /** Rendered dimmed, e.g. "Alles uit ..." */
  dim?: boolean;
}

interface TierConfig {
  id: string;
  nameKey: string;
  nameDefault: string;
  basePrice: number;
  /** Optional italic condition shown above the feature list */
  conditionKey?: string;
  conditionDefault?: string;
  /** Show a "Vanaf" prefix before the price */
  fromPrice?: boolean;
  /** Optional small note shown under the price */
  noteKey?: string;
  noteDefault?: string;
  features: FeatureDef[];
}

interface ModuleBlockConfig {
  id: string;
  numberLabel: string;
  nameKey: string;
  nameDefault: string;
  subtitleKey: string;
  subtitleDefault: string;
  headerBackground: string;
  spineBackground: string;
  tiers: TierConfig[];
}

/**
 * Performance management features: the AI-focused list is shown now and
 * switches automatically to the new list on 1 January 2027.
 */
const PM_FEATURES_SWITCH_DATE = new Date("2027-01-01T00:00:00+01:00");
const PM_FEATURES_SWITCHED = Date.now() < PM_FEATURES_SWITCH_DATE.getTime();

const modules: ModuleBlockConfig[] = [
  {
    id: "workDesign",
    numberLabel: "01",
    nameKey: "modules.workDesign.name",
    nameDefault: "Work Design",
    subtitleKey: "modules.workDesign.subtitle",
    subtitleDefault: "functiehuis, niveaus en schalen",
    headerBackground: "linear-gradient(90deg, #7530CE 0%, #911FB6 100%)",
    spineBackground: "linear-gradient(180deg, #7530CE 0%, #911FB6 100%)",
    tiers: [
      {
        id: "foundations",
        nameKey: "plans.foundations.name",
        nameDefault: "Foundations",
        basePrice: 2,
        fromPrice: true,
        noteKey: "plans.foundations.note",
        noteDefault: "*prijs afhankelijk van functiewaarderingsmethodiek",
        features: [
          { k: "plans.foundations.f1", d: "Eigen functiehuis opbouwen met een eigen methodiek of importeren uit Styr of Gradar" },
          { k: "plans.foundations.f2", d: "Functieprofielen genereren" },
          { k: "plans.foundations.f3", d: "Competentie- en skillframeworks" },
          { k: "plans.foundations.f4", d: "Functieprofiel per medewerker" },
          { k: "plans.foundations.f5", d: "Salarisbanden aanmaken of importeren" },
          { k: "plans.foundations.f6", d: "Salary band-analyse" },
          { k: "plans.foundations.f7", d: "Positie van medewerkers binnen de salarisschaal" },
          { k: "plans.foundations.f8", d: "Medewerkersdata importeren via CSV of HR-systeem" },
          { k: "plans.foundations.f9", d: "Variabel loon en totale beloning" },
          { k: "plans.foundations.f10", d: "Benchmark- en marktdata" },
        ],
      },
    ],
  },
  {
    id: "performanceManagement",
    numberLabel: "02",
    nameKey: "modules.performanceManagement.name",
    nameDefault: "Performance Management",
    subtitleKey: "modules.performanceManagement.subtitle",
    subtitleDefault: "evaluaties, doelen en ontwikkeling",
    headerBackground: "#AA7DF2",
    spineBackground: "#AA7DF2",
    tiers: [
      {
        id: "professional",
        nameKey: "plans.professional.name",
        nameDefault: "Professional",
        basePrice: 8,
        features: PM_FEATURES_SWITCHED
          ? [
              { k: "plans.professional.f1", d: "Genereer ieder profiel met AI" },
              { k: "plans.professional.f2", d: "360-graden evaluaties" },
              { k: "plans.professional.f3", d: "Ontwikkeldoelen & AI-chat" },
              { k: "plans.professional.f4", d: "Organisatie- en teamdoelen" },
              { k: "plans.professional.f5", d: "1:1s & teammeetings" },
              { k: "plans.professional.f6", d: "Carrièrepaden" },
              { k: "plans.professional.f7", d: "Rapportages" },
            ]
          : [
              { k: "plans.professional.c1", d: "(360°) Evaluaties op basis van functieprofiel en doelstellingen" },
              { k: "plans.professional.c2", d: "Functieprofielen opstellen op basis van AI" },
              { k: "plans.professional.c3", d: "Evaluaties automatisch voorbereiden met AI" },
              { k: "plans.professional.c4", d: "Feedback geven via Slack, Teams, WhatsApp of e-mail" },
              { k: "plans.professional.c5", d: "Assistent die gesprekken opneemt en samenvatting uitschrijft" },
              { k: "plans.professional.c6", d: "Evaluaties en doelstellingen opvolgen met 1:1's" },
            ],
      },
      {
        id: "advanced",
        nameKey: "plans.advanced.name",
        nameDefault: "Advanced",
        basePrice: 10,
        features: PM_FEATURES_SWITCHED
          ? [
              { k: "plans.advanced.f1", d: "Alles uit Professional +", dim: true },
              { k: "plans.advanced.f2", d: "Feedback ophalen uit e-mail, Slack & MS Teams (AI)" },
              { k: "plans.advanced.f3", d: "Reviews automatisch invullen (AI)" },
              { k: "plans.advanced.f4", d: "Gesprekstips voor managers (AI)" },
            ]
          : [
              { k: "plans.advanced.c1", d: "Alles uit Professional", dim: true },
              { k: "plans.advanced.c2", d: "Ontwikkelplannen binnen huidige functie" },
              { k: "plans.advanced.c3", d: "Carrièrepaden" },
              { k: "plans.advanced.c4", d: "Ambitie en groeiplannen" },
              { k: "plans.advanced.c5", d: "Verbetertrajecten" },
              { k: "plans.advanced.c6", d: "Koppeling met leerplatforms zoals Rakoo en Studytube" },
            ],
      },
      {
        id: "expert",
        nameKey: "plans.expert.name",
        nameDefault: "Expert",
        basePrice: 12,
        features: PM_FEATURES_SWITCHED
          ? [
              { k: "plans.expert.f1", d: "Alles uit Advanced +", dim: true },
              { k: "plans.expert.f2", d: "Voice-to-text in evaluaties" },
              { k: "plans.expert.f3", d: "Voice-to-text in 1:1s" },
              { k: "plans.expert.f4", d: "Rapportages analyseren (AI)" },
            ]
          : [
              { k: "plans.expert.c1", d: "Alles uit Advanced", dim: true },
              { k: "plans.expert.c2", d: "Vlootschouw & Talent review" },
              { k: "plans.expert.c3", d: "Kalibreren van evaluaties" },
              { k: "plans.expert.c4", d: "Data en rapportages analyseren met AI" },
            ],
      },
    ],
  },
  {
    id: "compensation",
    numberLabel: "03",
    nameKey: "modules.compensation.name",
    nameDefault: "Compensation",
    subtitleKey: "modules.compensation.subtitle",
    subtitleDefault: "belonen en loontransparantie",
    headerBackground: "linear-gradient(90deg, #FB9A24 0%, #FFC95E 100%)",
    spineBackground: "linear-gradient(180deg, #FB9A24 0%, #FFC95E 100%)",
    tiers: [
      {
        id: "compFoundations",
        nameKey: "plans.compFoundations.name",
        nameDefault: "Foundations",
        basePrice: 3,
        conditionKey: "plans.compFoundations.condition",
        conditionDefault: "Inzicht in beloning",
        features: [
          { k: "plans.compFoundations.f1", d: "Positie ten opzichte van collega's in gelijkwaardige functies" },
          { k: "plans.compFoundations.f2", d: "Juridische rapportages met AI" },
          { k: "plans.compFoundations.f3", d: "(On)gecorrigeerde loonkloofanalyse per functie en niveau" },
          { k: "plans.compFoundations.f4", d: "Analyse van variabelen achter loonverschillen" },
        ],
      },
      {
        id: "compAdvanced",
        nameKey: "plans.compAdvanced.name",
        nameDefault: "Advanced",
        basePrice: 6,
        conditionKey: "plans.compAdvanced.condition",
        conditionDefault: "Salarisrondes draaien",
        features: [
          { k: "plans.compAdvanced.f1", d: "Alles uit Foundations", dim: true },
          { k: "plans.compAdvanced.f2", d: "Salarisronde op basissalaris" },
          { k: "plans.compAdvanced.f3", d: "Off-cycle reviews" },
          { k: "plans.compAdvanced.f4", d: "Realtime budgetbewaking" },
          { k: "plans.compAdvanced.f5", d: "Sandbox voor scenario's" },
          { k: "plans.compAdvanced.f6", d: "Realtime impact op loongelijkheid tijdens de ronde" },
          { k: "plans.compAdvanced.f7", d: "Regels voor wie in aanmerking komt" },
          { k: "plans.compAdvanced.f8", d: "Aanbevelingsregels, zoals merit" },
          { k: "plans.compAdvanced.f9", d: "Goedkeuringsflows voor managers" },
          { k: "plans.compAdvanced.f10", d: "Automatische verhogingsbrieven" },
          { k: "plans.compAdvanced.f11", d: "Salarisrondes importeren en exporteren via CSV" },
          { k: "plans.compAdvanced.f12", d: "Salarisrondes synchroniseren via API" },
        ],
      },
      {
        id: "compExpert",
        nameKey: "plans.compExpert.name",
        nameDefault: "Expert",
        basePrice: 9,
        conditionKey: "plans.compExpert.condition",
        conditionDefault: "Variabel loon en bonus",
        features: [
          { k: "plans.compExpert.f1", d: "Alles uit Advanced", dim: true },
          { k: "plans.compExpert.f2", d: "Salarisronde op variabel loon, bonussen en componenten" },
        ],
      },
    ],
  },
];

const SURVEYS_PRICE = 4;
const ALLINONE_PRICE = 20;

const COLUMN_BACKGROUNDS: Record<string, string> = {
  workDesign: "linear-gradient(135deg, #7530CE 0%, #911FB6 100%)",
  performanceManagement: "#AA7DF2",
  compensation: "linear-gradient(135deg, #FB9A24 0%, #FFC95E 100%)",
};

/** Rounds to two decimals, half up (2,475 -> 2,48). */
const roundHalfUp = (value: number): number =>
  Math.floor(value * 100 + 0.5 + 1e-9) / 100;

const discountFor = (count: number): number => {
  if (count > 500) return 0;
  if (count > 300) return 0.225;
  if (count > 200) return 0.175;
  if (count > 100) return 0.10;
  return 0;
};

const priceFor = (basePrice: number, count: number): number =>
  roundHalfUp(basePrice * (1 - discountFor(count)));

const formatPrice = (price: number): string => `€${price.toFixed(2).replace('.', ',')}`;

/** Compact tier row used in the four-column builder. */
const CompactTierRow = ({
  moduleId,
  tier,
  selected,
  onToggle,
  price,
  onRequest,
}: {
  moduleId: string;
  tier: TierConfig;
  selected: boolean;
  onToggle: () => void;
  price: number;
  onRequest: boolean;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="rounded-xl transition-colors"
      style={{
        border: `1px solid ${selected ? "#7530CE" : "#E9E2F5"}`,
        background: selected ? "#F9F7FD" : "#FFFFFF",
        padding: "14px 16px",
      }}
    >
      <div
        role="button"
        tabIndex={0}
        aria-pressed={selected}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        className="cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <span
            className="shrink-0 rounded-full flex items-center justify-center"
            style={{
              width: "20px",
              height: "20px",
              border: `1px solid ${selected ? "#7530CE" : "#C9BCE4"}`,
              background: selected ? "#7530CE" : "transparent",
            }}
          >
            {selected && <Check className="w-3 h-3 text-white" />}
          </span>
          <T
            page={PAGE}
            k={tier.nameKey}
            d={tier.nameDefault}
            as="span"
            className="text-foreground min-w-0"
            style={{ fontSize: "15px", fontWeight: 600 }}
          />
        </div>
        <div className="mt-2">
          <span className="text-foreground" style={{ fontSize: "22px", fontWeight: 600 }}>
            {onRequest ? (
              <T page={PAGE} k="calculator.onRequest" d="Prijs op aanvraag" />
            ) : (
              <>
                {tier.fromPrice && (
                  <T
                    page={PAGE}
                    k="fromPrefix"
                    d="Vanaf"
                    as="span"
                    style={{ fontSize: "14px", fontWeight: 500, marginRight: "4px" }}
                  />
                )}
                {formatPrice(price)}
              </>
            )}
          </span>
          {!onRequest && (
            <span className="block" style={{ fontSize: "12px", color: "#6F677E" }}>
              <T page={PAGE} k="perMonthShort" d="p/m per medewerker" />
            </span>
          )}
          {tier.noteKey && (
            <T
              page={PAGE}
              k={tier.noteKey}
              d={tier.noteDefault}
              as="span"
              className="block italic"
              style={{ fontSize: "11px", color: "#6F677E", marginTop: "4px" }}
            />
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="mt-2 inline-flex items-center gap-1"
        style={{ fontSize: "12px", color: "#7530CE", fontWeight: 500 }}
      >
        <T page={PAGE} k="featuresToggle" d="Features" />
        <ChevronRight
          className="w-3 h-3 transition-transform duration-200"
          style={{ transform: open ? "rotate(90deg)" : "none" }}
        />
      </button>
      <div
        className="overflow-hidden transition-[max-height,opacity] duration-200"
        style={{ maxHeight: open ? "640px" : "0px", opacity: open ? 1 : 0 }}
      >
        <div className="pt-3">
          {tier.conditionKey && (
            <T
              page={PAGE}
              k={tier.conditionKey}
              d={tier.conditionDefault}
              as="p"
              className="text-xs italic text-muted-foreground mb-2"
            />
          )}
          <ul className="space-y-2">
            {tier.features.map((feature) => (
              <li key={feature.k} className="flex items-start gap-2">
                <div className="flex-shrink-0 w-4 h-4 rounded-full bg-[#F3EDFD] flex items-center justify-center mt-0.5">
                  <Check className="w-2.5 h-2.5 text-primary" />
                </div>
                <T
                  page={PAGE}
                  k={feature.k}
                  d={feature.d}
                  as="span"
                  className={cn("text-xs", feature.dim ? "text-muted-foreground italic" : "text-foreground")}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
      <span className="sr-only">{moduleId}</span>
    </div>
  );
};

const Pricing = () => {
  const { i18n } = useTranslation();
  const [employeeCount, setEmployeeCount] = useState(100);
  const bundleCount = employeeCount;
  const setBundleCount = setEmployeeCount;
  const [selection, setSelection] = useState<Record<string, string | null>>({});
  const [surveysSelected, setSurveysSelected] = useState(false);

  const toggleTier = (moduleId: string, tierId: string) =>
    setSelection((prev) => ({ ...prev, [moduleId]: prev[moduleId] === tierId ? null : tierId }));



  // Load HubSpot meetings embed script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js';
    script.type = 'text/javascript';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  void i18n;

  const isOnRequest = employeeCount > 500;

  const discount = discountFor(employeeCount);
  const hasDiscount = discount > 0;

  const getPrice = (basePrice: number): number => priceFor(basePrice, employeeCount);

  const getTotalPrice = (basePrice: number): string => {
    const total = getPrice(basePrice) * employeeCount;
    return `€${Math.round(total).toLocaleString('nl-NL')}`;
  };

  /** Bundle block has its own, independent employee slider. */
  const bundleOnRequest = bundleCount > 500;
  const bundleDiscount = discountFor(bundleCount);
  const bundleGetPrice = (basePrice: number): number => priceFor(basePrice, bundleCount);
  const bundleTotalPrice = (basePrice: number): string =>
    `€${Math.round(bundleGetPrice(basePrice) * bundleCount).toLocaleString('nl-NL')}`;

  // All-in-one = WD Foundations (2) + PM Expert (12) + Compensation Expert (9) + Surveys (4)
  const bundleParts = [2, 12, 9, 4].map((p) => bundleGetPrice(p));
  const bundleSum = roundHalfUp(bundleParts.reduce((a, b) => a + b, 0));
  const bundlePrice = bundleGetPrice(ALLINONE_PRICE);
  const bundleDiscountPct = Math.round(((bundleSum - bundlePrice) / bundleSum) * 100);
  void bundleDiscount;

  const allInOneBundle = getPrice(ALLINONE_PRICE);

  const selectedItems = modules
    .map((module) => {
      const tier = module.tiers.find((t) => t.id === selection[module.id]);
      return tier ? { module, tier } : null;
    })
    .filter((x): x is { module: ModuleBlockConfig; tier: TierConfig } => x !== null);

  const subtotal = roundHalfUp(
    selectedItems.reduce((sum, { tier }) => sum + getPrice(tier.basePrice), 0) +
      (surveysSelected ? getPrice(SURVEYS_PRICE) : 0)
  );
  const hasSelection = selectedItems.length > 0 || surveysSelected;
  const hasFromPrice = selectedItems.some(({ tier }) => tier.fromPrice);
  const needsWorkDesign = !!selection["compensation"] && !selection["workDesign"];
  
  const showAllInOneHint = hasSelection && subtotal >= allInOneBundle;


  const discountLabel = `-${(discount * 100) % 1 === 0
    ? Math.round(discount * 100)
    : (discount * 100).toFixed(1).replace('.', ',')}%`;

  const sliderMarks = [50, 100, 200, 300, 400];
  const SLIDER_MIN = 50;
  const SLIDER_MAX = 501;

  const DiscountBadge = () =>
    hasDiscount && !isOnRequest ? (
      <Badge className="ml-2 bg-green-100 text-green-700 border-0 text-xs">{discountLabel}</Badge>
    ) : null;

  const PriceCta = ({ variant = "solid" }: { variant?: "solid" | "outline" }) => (
    <Button
      variant={variant === "outline" ? "outline" : "default"}
      className={cn(
        "rounded-full w-full mt-auto",
        variant === "solid" && "bg-primary text-primary-foreground hover:bg-primary/90"
      )}
      onClick={() => document.getElementById('schedule-demo')?.scrollIntoView({ behavior: 'smooth' })}
    >
      {isOnRequest
        ? <T page={PAGE} k="ctaContact" d="Neem contact op" />
        : <T page={PAGE} k="cta" d="Plan een demo" />}
      <ArrowRight className="w-4 h-4 ml-2" />
    </Button>
  );

  const FeatureList = ({ features, onDark = false, columns = 1 }: { features: FeatureDef[]; onDark?: boolean; columns?: 1 | 2 }) => (
    <ul className={cn("space-y-3 flex-1", columns === 2 && "md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-3 md:space-y-0")}>

      {features.map((feature) => (
        <li key={feature.k} className="flex items-start gap-3">
          <div className={cn(
            "flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5",
            onDark ? "bg-white/20" : "bg-[#F3EDFD]"
          )}>
            <Check className={cn("w-3 h-3", onDark ? "text-white" : "text-primary")} />
          </div>
          <T
            page={PAGE}
            k={feature.k}
            d={feature.d}
            as="span"
            className={cn(
              "text-sm",
              feature.dim
                ? (onDark ? "text-white/50 italic" : "text-muted-foreground italic")
                : (onDark ? "text-white/90" : "text-foreground")
            )}
          />
        </li>
      ))}
    </ul>
  );

  const TierRow = ({
    moduleId,
    tier,
    twoColFeatures = false,
  }: { moduleId: string; tier: TierConfig; twoColFeatures?: boolean }) => {
    const isSelected = selection[moduleId] === tier.id;
    return (
      <div
        role="button"
        tabIndex={0}
        aria-pressed={isSelected}
        onClick={() => toggleTier(moduleId, tier.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleTier(moduleId, tier.id);
          }
        }}
        className="bg-white rounded-xl cursor-pointer transition-colors flex-1 min-w-0 flex flex-col"
        style={{
          border: `1px solid ${isSelected ? "#7530CE" : "#E9E2F5"}`,
          background: isSelected ? "#F9F7FD" : "#FFFFFF",
          padding: "16px 18px",
        }}
      >
        <div className="flex items-start gap-3">
          <span
            className="shrink-0 rounded-full flex items-center justify-center mt-0.5"
            style={{
              width: "22px",
              height: "22px",
              border: isSelected ? "1px solid #7530CE" : "1px solid #C9BCE4",
              background: isSelected ? "#7530CE" : "transparent",
            }}
          >
            {isSelected && <Check className="w-3 h-3 text-white" />}
          </span>
          <T
            page={PAGE}
            k={tier.nameKey}
            d={tier.nameDefault}
            as="span"
            className="block text-foreground min-w-0"
            style={{ fontSize: "16px", fontWeight: 600 }}
          />
        </div>
        <div className="mt-3">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-foreground" style={{ fontSize: "24px", fontWeight: 600 }}>
              {isOnRequest ? (
                <T page={PAGE} k="calculator.onRequest" d="Prijs op aanvraag" />
              ) : (
                <>
                  {tier.fromPrice && (
                    <T
                      page={PAGE}
                      k="fromPrefix"
                      d="Vanaf"
                      as="span"
                      style={{ fontSize: "15px", fontWeight: 500, marginRight: "5px" }}
                    />
                  )}
                  {formatPrice(getPrice(tier.basePrice))}
                </>
              )}
            </span>
            <DiscountBadge />
          </div>
          {!isOnRequest && (
            <span className="block" style={{ fontSize: "12px", color: "#6F677E" }}>
              <T page={PAGE} k="perMonthShort" d="p/m per medewerker" />
            </span>
          )}
          {tier.noteKey && (
            <T
              page={PAGE}
              k={tier.noteKey}
              d={tier.noteDefault}
              as="span"
              className="block italic"
              style={{ fontSize: "11px", color: "#6F677E", marginTop: "4px" }}
            />
          )}
        </div>
        {tier.conditionKey && (
          <T
            page={PAGE}
            k={tier.conditionKey}
            d={tier.conditionDefault}
            as="p"
            className="text-xs italic text-muted-foreground mt-3"
          />
        )}
        <div className="mt-3">
          <FeatureList features={tier.features} columns={twoColFeatures ? 2 : 1} />
        </div>
      </div>
    );
  };


  const PriceBlock = ({
    basePrice,
    listPrice,
    size = "lg",
  }: { basePrice: number; listPrice?: number; size?: "lg" | "md" }) => (
    <>
      <div className="flex items-baseline gap-1 mb-1 flex-wrap">
        {listPrice !== undefined && !isOnRequest && (
          <span className="text-lg text-muted-foreground line-through mr-1">
            {formatPrice(getPrice(listPrice))}
          </span>
        )}
        <span className={cn(
          "font-bold text-foreground",
          isOnRequest ? "text-2xl" : size === "lg" ? "text-4xl" : "text-3xl"
        )}>
          {isOnRequest
            ? <T page={PAGE} k="calculator.onRequest" d="Prijs op aanvraag" />
            : formatPrice(getPrice(basePrice))}
        </span>
        <DiscountBadge />
        {!isOnRequest && (
          <span className="text-sm text-muted-foreground">
            <T page={PAGE} k="period" d="per medewerker per maand" />
          </span>
        )}
      </div>
      {!isOnRequest && (
        <p className="text-sm font-medium text-muted-foreground">
          {getTotalPrice(basePrice)} <T page={PAGE} k="calculator.total" d="totaal per maand*" />
        </p>
      )}
    </>
  );

  const renderSlider = (value: number, onChange: (v: number) => void, dark: boolean) => (
    <div className="flex flex-col md:flex-row items-center gap-8">
      <div className="flex-1 w-full">
        <Slider
          value={[value]}
          onValueChange={(v) => onChange(v[0])}
          min={SLIDER_MIN}
          max={SLIDER_MAX}
          step={1}
          className="w-full"
        />
        <div className={cn("relative text-xs mt-2 h-4", dark ? "text-white/60" : "text-muted-foreground")}>
          {sliderMarks.map((mark) => {
            const pct = ((mark - SLIDER_MIN) / (SLIDER_MAX - SLIDER_MIN)) * 100;
            return (
              <span
                key={mark}
                className="absolute"
                style={{
                  left: `calc(10px + ${pct} * (100% - 20px) / 100)`,
                  transform: mark === SLIDER_MIN ? undefined : 'translateX(-50%)',
                }}
              >
                {mark}
              </span>
            );
          })}
          <span className="absolute right-0">500+</span>

        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <div className={cn("w-12 h-12 rounded-full flex items-center justify-center", dark ? "bg-white/10" : "bg-[#F3EDFD]")}>
          <Users className={cn("w-6 h-6", dark ? "text-[#FFC95E]" : "text-primary")} />
        </div>
        <div className="whitespace-nowrap">
          <span
            className={cn("text-4xl font-bold tabular-nums inline-block min-w-[5ch] text-right", !dark && "text-primary")}
            style={dark ? { color: "#FFC95E" } : undefined}
          >
            {value > 500 ? '500+' : value}
          </span>
          <span className={cn("ml-2", dark ? "text-white/70" : "text-muted-foreground")}>
            <T page={PAGE} k="calculator.employees" d="medewerkers" />
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* 1 — Hero */}
      <section className="w-full px-6 pt-[140px] pb-16 md:pb-24">
        <div className="w-full sm:w-[80%] max-w-7xl mx-auto text-center">
          <T
            page={PAGE}
            k="hero.eyebrow"
            d="Tarieven"
            as="span"
            className="block uppercase"
            style={{ fontSize: "13px", letterSpacing: "0.08em", color: "#7530CE", fontWeight: 600 }}
          />
          <T
            page={PAGE}
            k="hero.title"
            d="Kies een pakket dat bij jou past"
            as="h1"
            className="text-foreground mt-4"
          />
          <T
            page={PAGE}
            k="hero.intro"
            d="Elke organisatie is anders, en dus ook wat je nodig hebt. Groei op jouw tempo: ga voor de all-in-one deal met alles inbegrepen, of stel zelf een pakket samen per module."
            as="p"
            className="text-lg mx-auto mt-5"
            style={{ color: "#46315D", maxWidth: "720px" }}
          />
          <div className="flex flex-wrap justify-center mt-8" style={{ gap: "16px" }}>
            <Button
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-7 py-6"
              onClick={() => document.getElementById('all-in-one')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <T page={PAGE} k="hero.ctaBundle" d="Bekijk all-in-one" />
            </Button>
            <Button
              variant="outline"
              className="rounded-full bg-white px-7 py-6"
              onClick={() => document.getElementById('zelf-samenstellen')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <T page={PAGE} k="hero.ctaBuilder" d="Stel je eigen pakket samen" />
            </Button>
          </div>
        </div>
      </section>

      {/* 2 — Stel zelf je bundel samen */}
      <section id="zelf-samenstellen" className="w-full scroll-mt-36 px-6 py-16" style={{ background: "#FFFFFF" }}>
        <div className="w-full sm:w-[80%] max-w-7xl mx-auto">
          <T
            page={PAGE}
            k="builder.eyebrow"
            d="Zelf samenstellen"
            as="span"
            className="block uppercase"
            style={{ fontSize: "13px", letterSpacing: "0.08em", color: "#7530CE", fontWeight: 600 }}
          />
          <T
            page={PAGE}
            k="builder.title"
            d="Stel je pakket samen in vier stappen"
            as="h2"
            className="feature-section-heading text-left"
            style={{ color: "#2A2334", marginTop: "8px" }}
          />
          <T
            page={PAGE}
            k="builder.subtitle"
            d="Kies per module het pakket dat past. Je ziet direct wat het samen kost."
            as="p"
            style={{ color: "#46315D" }}
          />

          {/* Stap 1 — medewerkers */}
          <div className="mt-8 mb-6 rounded-2xl" style={{ background: "#F3EDFD", padding: "24px" }}>
            <T
              page={PAGE}
              k="step1.label"
              d="Stap 1"
              as="span"
              className="block uppercase"
              style={{ fontSize: "11px", letterSpacing: "0.08em", color: "#7530CE", fontWeight: 600 }}
            />
            <T
              page={PAGE}
              k="calculator.title"
              d="Hoeveel medewerkers heeft jouw organisatie?"
              as="h3"
              className="mb-5"
              style={{ fontSize: "20px", color: "#2A2334", marginTop: "6px" }}
            />
            {renderSlider(employeeCount, setEmployeeCount, false)}
            {isOnRequest && (
              <THtml
                page={PAGE}
                k="calculator.note"
                d="Voor organisaties met meer dan 500 medewerkers bieden we maatwerk. Neem contact op voor een offerte."
                options={{ postProcess: "pricingContactLink", contactLabel: i18n.t(`${PAGE}.ctaContact`, { defaultValue: "Neem contact op" }), contactHref: `/${i18n.language.split("-")[0]}/contact/` }}
                as="p"
                className="text-muted-foreground text-center max-w-lg mx-auto mt-6"
              />
            )}
          </div>

          {/* Stappen 2-4 + bundelkolom */}
          <div className="grid grid-cols-1 lg:grid-cols-4 items-start" style={{ gap: "20px" }}>
            {modules.map((module, idx) => {
              const stepNo = idx + 2;
              return (
                <div key={module.id}>
                  <div
                    className="rounded-2xl flex flex-col justify-center min-h-[96px] lg:min-h-[132px]"
                    style={{
                      background: COLUMN_BACKGROUNDS[module.id],
                      padding: "16px 18px",
                    }}
                  >
                    <span
                      className="uppercase text-white/70"
                      style={{ fontSize: "11px", letterSpacing: "0.08em", fontWeight: 600 }}
                    >
                      <T page={PAGE} k={`step${stepNo}.label`} d={`Stap ${stepNo}`} />
                    </span>
                    <T
                      page={PAGE}
                      k={module.nameKey}
                      d={module.nameDefault}
                      as="span"
                      className="block text-white [overflow-wrap:anywhere]"
                      style={{ fontSize: "18px", fontWeight: 600, lineHeight: 1.3 }}
                    />
                    <T
                      page={PAGE}
                      k={module.subtitleKey}
                      d={module.subtitleDefault}
                      as="span"
                      className="block text-white/75"
                      style={{ fontSize: "12px", lineHeight: 1.4 }}
                    />
                  </div>

                  <div className="flex flex-col mt-3" style={{ gap: "10px" }}>
                    {module.tiers.map((tier) => (
                      <CompactTierRow
                        key={tier.id}
                        moduleId={module.id}
                        tier={tier}
                        selected={selection[module.id] === tier.id}
                        onToggle={() => toggleTier(module.id, tier.id)}
                        price={getPrice(tier.basePrice)}
                        onRequest={isOnRequest}
                      />
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Kolom 4 — Jouw pakket */}
            <div className="lg:sticky" style={{ top: "96px" }}>
              <div
                className="bg-white rounded-2xl"
                style={{ border: "1px solid #E9E2F5", padding: "20px" }}
              >
                <T
                  page={PAGE}
                  k="summary.title"
                  d="Jouw pakket"
                  as="h3"
                  style={{ fontSize: "17px", fontWeight: 600, color: "#2A2334" }}
                />
                <div className="mt-4 flex flex-col gap-3">
                  {!hasSelection && (
                    <T
                      page={PAGE}
                      k="summary.empty"
                      d="Doorloop de stappen hiernaast."
                      as="p"
                      style={{ color: "#6F677E", fontSize: "13px" }}
                    />
                  )}
                  {selectedItems.map(({ module, tier }) => (
                    <div key={module.id} className="flex items-start justify-between gap-3">
                      <span className="min-w-0">
                        <T
                          page={PAGE}
                          k={module.nameKey}
                          d={module.nameDefault}
                          as="span"
                          className="block"
                          style={{ fontSize: "12px", color: "#6F677E" }}
                        />
                        <T
                          page={PAGE}
                          k={tier.nameKey}
                          d={tier.nameDefault}
                          as="span"
                          className="block"
                          style={{ fontSize: "14px", color: "#2A2334", fontWeight: 500 }}
                        />
                      </span>
                      {!isOnRequest && (
                        <span className="shrink-0" style={{ fontSize: "14px", color: "#2A2334" }}>
                          {formatPrice(getPrice(tier.basePrice))}
                        </span>
                      )}
                    </div>
                  ))}
                  {surveysSelected && (
                    <div className="flex items-start justify-between gap-3">
                      <span className="min-w-0">
                        <T
                          page={PAGE}
                          k="addonLabel"
                          d="Add-on"
                          as="span"
                          className="block"
                          style={{ fontSize: "12px", color: "#6F677E" }}
                        />
                        <T
                          page={PAGE}
                          k="addon.name"
                          d="Surveys"
                          as="span"
                          className="block"
                          style={{ fontSize: "14px", color: "#2A2334", fontWeight: 500 }}
                        />
                      </span>
                      {!isOnRequest && (
                        <span className="shrink-0" style={{ fontSize: "14px", color: "#2A2334" }}>
                          {formatPrice(getPrice(SURVEYS_PRICE))}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-5 pt-5" style={{ borderTop: "1px solid #E9E2F5" }}>
                  <T
                    page={PAGE}
                    k="summary.subtotalLabel"
                    d="Totaal per medewerker"
                    as="span"
                    className="block"
                    style={{ fontSize: "12px", color: "#6F677E" }}
                  />
                  <span className="block" style={{ fontSize: "30px", fontWeight: 600, color: "#2A2334", lineHeight: 1.1 }}>
                    {isOnRequest ? (
                      <T page={PAGE} k="calculator.onRequest" d="Prijs op aanvraag" />
                    ) : (
                      <>
                        {hasFromPrice && (
                          <T
                            page={PAGE}
                            k="fromPrefix"
                            d="Vanaf"
                            as="span"
                            style={{ fontSize: "16px", fontWeight: 500, marginRight: "6px" }}
                          />
                        )}
                        {formatPrice(subtotal)}
                      </>
                    )}
                  </span>
                  {!isOnRequest && (
                    <>
                      <T
                        page={PAGE}
                        k="summary.perMonth"
                        d="per maand"
                        as="span"
                        className="block"
                        style={{ fontSize: "12px", color: "#6F677E" }}
                      />
                      <p className="mt-2" style={{ fontSize: "13px", color: "#46315D" }}>
                        {hasFromPrice && (
                          <>
                            <T page={PAGE} k="fromPrefix" d="Vanaf" as="span" />{" "}
                          </>
                        )}
                        {`€${Math.round(subtotal * employeeCount).toLocaleString('nl-NL')}`}{" "}
                         <T page={PAGE} k="calculator.total" d="totaal per maand*" />
                      </p>
                    </>
                  )}

                  {needsWorkDesign && (
                    <T
                      page={PAGE}
                      k="summary.requiresWorkDesign"
                      d="Compensation werkt ook zonder Work Design, maar zonder salarisschalen haal je er minder uit."
                      as="p"
                      className="mt-3"
                      style={{ color: "#46315D", fontSize: "13px" }}
                    />
                  )}
                  {!isOnRequest && showAllInOneHint && (
                    <p className="mt-3" style={{ color: "#46315D", fontSize: "13px" }}>
                      <a
                        href="#all-in-one"
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById('all-in-one')?.scrollIntoView({ behavior: 'smooth' });
                        }}
                        className="underline font-medium"
                      >
                        <T page={PAGE} k="allinone.name" d="All-in-one" />
                      </a>{" "}
                      <T page={PAGE} k="summary.allInOneHintPre" d="kost" as="span" />{" "}
                      {formatPrice(allInOneBundle)}{" "}
                      <T page={PAGE} k="summary.allInOneHintPost" d="per medewerker en bevat alles." as="span" />
                    </p>
                  )}

                  <Button
                    className="rounded-full w-full mt-5 bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => document.getElementById('schedule-demo')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    {isOnRequest
                      ? <T page={PAGE} k="ctaContact" d="Neem contact op" />
                      : <T page={PAGE} k="cta" d="Plan een demo" />}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Add-ons */}
          <div className="mt-8">
            <T
              page={PAGE}
              k="addons.heading"
              d="Add-ons"
              as="h3"
              className="mb-3"
              style={{ fontSize: "17px", fontWeight: 600, color: "#2A2334" }}
            />
            <div
              className="bg-white rounded-2xl flex flex-wrap items-center justify-between gap-6 cursor-pointer"
              style={{
                border: `1px solid ${surveysSelected ? "#7530CE" : "#E9E2F5"}`,
                background: surveysSelected ? "#F9F7FD" : "#FFFFFF",
                padding: "28px 32px",
              }}
              role="button"
              tabIndex={0}
              aria-pressed={surveysSelected}
              onClick={() => setSurveysSelected((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSurveysSelected((v) => !v);
                }
              }}
            >
              <div className="flex items-center gap-4">
                <span
                  className="shrink-0 rounded-md flex items-center justify-center"
                  style={{
                    width: "22px",
                    height: "22px",
                    border: surveysSelected ? "1px solid #7530CE" : "1px solid #C9BCE4",
                    background: surveysSelected ? "#7530CE" : "transparent",
                  }}
                >
                  {surveysSelected && <Check className="w-3 h-3 text-white" />}
                </span>
                <div className="w-10 h-10 rounded-lg bg-[#FDF2ED] flex items-center justify-center shrink-0">
                  <Zap className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <Badge variant="outline" className="text-xs border-orange-300 text-orange-600 mb-1">
                    <T page={PAGE} k="addonLabel" d="Add-on" />
                  </Badge>
                  <T page={PAGE} k="addon.name" d="Surveys" as="h3" className="text-lg font-semibold text-foreground" />
                  <T
                    page={PAGE}
                    k="addon.summary"
                    d="Pulse surveys, MTO's en rapportages met AI"
                    as="p"
                    className="text-sm"
                    style={{ color: "#46315D" }}
                  />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-6">
                <div>
                  <PriceBlock basePrice={SURVEYS_PRICE} size="md" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3 — All-in-one bundel, eigen slider */}
      <section id="all-in-one" className="w-full scroll-mt-36 px-6 py-16" style={{ background: "#2A2334" }}>
        <div className="w-full sm:w-[80%] max-w-7xl mx-auto">
          <T
            page={PAGE}
            k="bundle.sliderLabel"
            d="Voor hoeveel medewerkers?"
            as="span"
            className="block text-white mb-4"
            style={{ fontSize: "15px", fontWeight: 600 }}
          />
          {renderSlider(bundleCount, setBundleCount, true)}
          {bundleOnRequest && (
            <THtml
              page={PAGE}
              k="calculator.note"
              d="Voor organisaties met meer dan 500 medewerkers bieden we maatwerk. Neem contact op voor een offerte."
              options={{ postProcess: "pricingContactLink", contactLabel: i18n.t(`${PAGE}.ctaContact`, { defaultValue: "Neem contact op" }), contactHref: `/${i18n.language.split("-")[0]}/contact/` }}
              as="p"
              className="text-white/70 text-center max-w-lg mx-auto mt-6"
            />
          )}

          <div className="mt-10">
            <div
              className="rounded-3xl flex flex-col md:flex-row md:items-center gap-10"
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.15)",
                padding: "48px",
              }}
            >
              {/* Left column */}
              <div className="md:w-[55%]">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span
                    className="inline-block rounded-full text-xs font-medium px-3 py-1"
                    style={{ background: "#FFC95E", color: "#2A2334" }}
                  >
                    <T page={PAGE} k="bestDeal" d="Beste deal" />
                  </span>
                  {!bundleOnRequest && (
                    <span
                      className="inline-block rounded-full text-xs font-medium px-3 py-1 text-white"
                      style={{ background: "rgba(255,255,255,0.15)" }}
                    >
                      -{bundleDiscountPct}%
                    </span>
                  )}
                </div>
                <h2 className="text-white" style={{ fontSize: "44px", fontWeight: 600, lineHeight: 1.15 }}>
                  <T page={PAGE} k="allinone.name" d="All-in-one" />
                </h2>
                <div className="flex flex-wrap gap-2 mt-6">
                  {[
                    { k: "allinone.chip1", d: "Work Design Foundations" },
                    { k: "allinone.chip2", d: "Performance Management Expert" },
                    { k: "allinone.chip3", d: "Compensation Expert" },
                    { k: "allinone.chip4", d: "Surveys" },
                  ].map((chip) => (
                    <T
                      key={chip.k}
                      page={PAGE}
                      k={chip.k}
                      d={chip.d}
                      as="span"
                      className="rounded-full text-white"
                      style={{ background: "rgba(255,255,255,0.12)", padding: "6px 14px", fontSize: "14px" }}
                    />
                  ))}
                </div>
              </div>

              {/* Right column */}
              <div className="md:flex-1 md:text-right">
                {bundleOnRequest ? (
                  <>
                    <p className="text-white text-3xl font-semibold">
                      <T page={PAGE} k="calculator.onRequest" d="Prijs op aanvraag" />
                    </p>
                    <div className="mt-6 md:flex md:justify-end">
                      <Button
                        className="rounded-full text-base px-8 py-6 border-0 hover:opacity-90"
                        style={{ background: "linear-gradient(90deg, #FB9A24 0%, #FFC95E 100%)", color: "#2A2334" }}
                        onClick={() => document.getElementById('schedule-demo')?.scrollIntoView({ behavior: 'smooth' })}
                      >
                        <T page={PAGE} k="ctaContact" d="Neem contact op" />
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-baseline gap-3 md:justify-end flex-wrap">
                      <span className="text-white/50 line-through" style={{ fontSize: "22px", fontWeight: 600 }}>
                        {formatPrice(bundleSum)}
                      </span>
                      <span style={{ color: "#FFC95E", fontSize: "52px", fontWeight: 600, lineHeight: 1.05 }}>
                        {formatPrice(bundlePrice)}
                      </span>
                    </div>
                    <p className="text-white/75" style={{ fontSize: "15px" }}>
                      <T page={PAGE} k="period" d="per medewerker per maand" />
                    </p>
                    <p className="text-white/60 mt-1" style={{ fontSize: "15px" }}>
                      {bundleTotalPrice(ALLINONE_PRICE)} <T page={PAGE} k="calculator.total" d="totaal per maand*" />
                    </p>
                    <div className="mt-6 md:flex md:justify-end">
                      <Button
                        className="rounded-full text-base px-8 py-6 border-0 hover:opacity-90"
                        style={{ background: "linear-gradient(90deg, #FB9A24 0%, #FFC95E 100%)", color: "#2A2334" }}
                        onClick={() => document.getElementById('schedule-demo')?.scrollIntoView({ behavior: 'smooth' })}
                      >
                        <T page={PAGE} k="cta" d="Plan een demo" />
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4 — Voorwaarden */}
      <section className="w-full px-6 py-14" style={{ background: "#EDE8F7" }}>
        <div className="w-full sm:w-[80%] max-w-7xl mx-auto">
          <T
            page={PAGE}
            k="terms.title"
            d="Voorwaarden*"
            as="h3"
            className="mb-4"
            style={{ fontSize: "20px", color: "#2A2334" }}
          />
          <ul className="space-y-3 text-sm" style={{ color: "#46315D" }}>
            <li className="flex items-start gap-3">
              <span className="text-primary mt-0.5">•</span>
              <T page={PAGE} k="notes.implementationCost" d="Exclusief eenmalige implementatiekosten vanaf €1.999" as="span" />
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary mt-0.5">•</span>
              <T page={PAGE} k="notes.volumeDiscount" d="Volumekorting vanaf 101 medewerkers" as="span" />
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary mt-0.5">•</span>
              <T page={PAGE} k="notes.loyaltyDiscount" d="Loyaliteitskorting bij een meerjarencontract" as="span" />
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary mt-0.5">•</span>
              <T page={PAGE} k="notes.integration" d="Integreer Learned met personeelsadministratiesystemen vanaf €50 per maand. Minimale contractwaarde €4.800." as="span" />
            </li>
          </ul>
        </div>
      </section>






      <ScheduleDemo compactMobile />
    </>
  );
};

export default Pricing;
